import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../config/supabase';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { generateContractDoc } from '../utils/contractDoc';

export default function SignaturePage({ rentalId, settings }) {
  // Get agencyId from URL (?agency=...) so we can access the correct Supabase row
  const _sigAgencyId = new URLSearchParams(window.location.search).get('agency') || null;
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [agencyTgChatId, setAgencyTgChatId] = useState('');
  // Public agency info (incl. per-agency contractClauses) merged into settings
  // so the signed/downloaded contract shows THIS agency's clauses & identity.
  const [agencyPublic, setAgencyPublic] = useState(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  // Load rental data
  useEffect(() => {
    if (!rentalId) { 
      setLoading(false); 
      setNotFound(true); 
      return; 
    }

    const loadRental = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        // Ensure a session so RLS lets the public client read the contract.
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            await supabase.auth.signInAnonymously();
            await new Promise(r => setTimeout(r, 300));
          }
        } catch (_) {}

        const agencyId = _sigAgencyId || null;
        
        // Load contract details via secure SECURITY DEFINER RPC
        const { data: row, error: err } = await supabase
          .rpc('get_signing_contract', { p_id: rentalId, p_agency: agencyId });

        if (err) throw err;

        if (row) {
          const rObj = { id: row.id, ...row.data, agency_id: row.agency_id };
          setRental(rObj);
          if (rObj.clientSignature) {
            setAlreadySigned(true);
          }

          // Fetch agency Telegram Chat ID via secure public RPC
          const currentAgencyId = row.agency_id || agencyId;
          if (currentAgencyId) {
            try {
              const { data: reg, error: tgErr } = await supabase
                .rpc('get_agency_public_info', { p_agency: currentAgencyId });
              if (tgErr) throw tgErr;
              if (reg) {
                if (reg.telegramChatId) setAgencyTgChatId(reg.telegramChatId);
                // Strip null fields so they don't clobber existing settings.
                const clean = Object.fromEntries(
                  Object.entries(reg).filter(([, v]) => v !== null && v !== undefined)
                );
                setAgencyPublic(clean);
              }
            } catch (tgErr) {
              console.error('Failed to load agency Telegram credentials:', tgErr);
            }
          }
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Load rental error:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadRental();
  }, [rentalId, _sigAgencyId]);

  // Initialize canvas drawing listeners
  useEffect(() => {
    if (!canvasRef.current || loading || alreadySigned || success) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1e3a8a';
      ctx.lineWidth = 2.5;
    };
    resize();
    
    const getPoint = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches && e.touches[0];
      const clientX = touch ? touch.clientX : e.clientX;
      const clientY = touch ? touch.clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    };
    
    const start = (e) => {
      e.preventDefault();
      isDrawingRef.current = true;
      lastPointRef.current = getPoint(e);
    };
    
    const move = (e) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      const point = getPoint(e);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      lastPointRef.current = point;
    };
    
    const end = (e) => {
      e.preventDefault();
      isDrawingRef.current = false;
    };
    
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end, { passive: false });
    
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [loading, alreadySigned, success]);

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const isCanvasEmpty = () => {
    if (!canvasRef.current) return true;
    const ctx = canvasRef.current.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] !== 0) return false;
    }
    return true;
  };

  const submitSignature = async () => {
    if (isCanvasEmpty()) {
      alert('⚠️ Veuillez signer dans la zone prévue / يرجى التوقيع في المنطقة المخصصة');
      return;
    }
    
    setSigning(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const timestamp = new Date().toISOString();

      // Save signature via secure database RPC
      const { data: saveSuccess, error: updateErr } = await supabase
        .rpc('save_signing_signature', {
          p_id: rentalId,
          p_agency: _sigAgencyId || null,
          p_signature: dataUrl
        });

      if (updateErr) throw updateErr;
      if (!saveSuccess) throw new Error("Impossible d'enregistrer la signature");

      // Keep local state in sync so the downloadable contract is the SIGNED one.
      setRental(prev => ({ ...(prev || {}), clientSignature: dataUrl, signedAt: timestamp }));

      // Notify owner via Telegram
      try {
        await sendTelegramNotification(
          buildTelegramMessage('contract_signed', {
            clientName: rental.clientName, carName: rental.carName,
            amount: rental.totalAmount || 0, currency: settings.currency || 'MAD',
            startDate: rental.startDate, endDate: rental.endDate,
            isOpen: !!rental.isOpenContract,
            time: new Date().toLocaleString('fr-FR')
          }, settings?.telegramLang),
          'contract_signed',
          agencyTgChatId
        );
      } catch (tgErr) {
        console.error('Telegram notification error:', tgErr);
      }
      
      setSuccess(true);
    } catch (err) {
      console.error('Sign error:', err);
      alert('❌ Erreur lors de l\'enregistrement / خطأ في الحفظ\n\n' + err.message);
    } finally {
      setSigning(false);
    }
  };

  // Loading state
  // Contract link validity: once the rental period has ended, the link expires
  // and the contract can no longer be viewed or downloaded.
  const contractExpired = (() => {
    if (!rental || rental.isOpenContract) return false;
    const end = rental.endDate;
    if (!end) return false;
    const ts = new Date(`${end}T23:59:59`).getTime();
    return Number.isFinite(ts) && Date.now() > ts;
  })();

  // View/Download the signed recto-verso contract. We render it via the BROWSER's
  // native print engine (open the contract HTML → auto-print → "Save as PDF"),
  // which never produces a blank page — unlike html2canvas, which can output a
  // blank PDF (e.g. with modern oklch CSS colors or complex SVG).
  const handleDownloadContract = async () => {
    if (!rental || contractExpired) return;
    // Open the window INSIDE the click gesture so the popup blocker can't kill it.
    const win = window.open('', '_blank');
    setDownloading(true);
    const rWithSig = { ...rental, signedAt: rental.signedAt || new Date().toISOString() };
    try {
      // Recto (page 1) + Verso (terms page 2) only — التزام / NARSA excluded.
      // Merge this agency's public info (company identity + per-agency
      // contractClauses) over the base settings so the downloaded contract
      // shows the correct company's clauses even for anonymous signers.
      const effSettings = { ...settings, ...(agencyPublic || {}) };
      const html = await generateContractDoc(
        rWithSig,
        { settings: effSettings, cars: [], isAr: false, action: 'get_contract_html' }
      );
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        // Trigger the print/save dialog once it has rendered.
        setTimeout(() => { try { win.focus(); win.print(); } catch (_) {} }, 700);
      } else {
        alert('⚠️ Autorisez les pop-ups pour afficher le contrat / اسمح بالنوافذ المنبثقة.');
      }
    } catch (err) {
      console.error('Contract view error:', err);
      if (win) { try { win.close(); } catch (_) {} }
      alert('❌ ' + (err.message || 'Erreur / خطأ'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 font-medium">Chargement du contrat... / تحميل العقد...</p>
        </div>
      </div>
    );
  }

  // Not found
  if (notFound || !rental) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-red-100">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-red-900 mb-2">Contrat introuvable</h1>
          <p className="text-red-700 mb-1">العقد غير موجود</p>
          <p className="text-sm text-gray-500 mt-4">Le lien semble invalide ou expiré. Veuillez contacter l\'agence.</p>
        </div>
      </div>
    );
  }

  // Expired link — rental/validity period has ended. Block view + download.
  if (contractExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-amber-200">
          <div className="text-6xl mb-4">⌛</div>
          <h1 className="text-2xl font-bold text-amber-900 mb-2">Lien expiré</h1>
          <p className="text-amber-700 mb-3">انتهت صلاحية الرابط</p>
          <p className="text-sm text-gray-500">
            La période de location de ce contrat est terminée. Le contrat n'est plus accessible via ce lien.
            <br />انتهت مدة كراء هذا العقد ولم يعد من الممكن الوصول إليه عبر هذا الرابط.
          </p>
          {rental.endDate && (
            <p className="text-xs text-gray-400 mt-4">
              Expiré le : {new Date(rental.endDate).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Already signed
  if (alreadySigned && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-green-150">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-green-900 mb-2">Déjà signé</h1>
          <p className="text-green-700 mb-4">لقد تم التوقيع مسبقاً</p>
          {rental.clientSignature && (
            <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50/50">
              <p className="text-xs font-bold text-gray-500 mb-2">Votre signature / توقيعك:</p>
              <img src={rental.clientSignature} className="max-w-full mx-auto max-h-[120px]" alt="Client Signature" />
            </div>
          )}
          {rental.signedAt && (
            <p className="text-xs text-gray-400 mt-4">
              Signé le: {new Date(rental.signedAt).toLocaleString('fr-FR')}
            </p>
          )}
          {/* Signed & still valid → allow viewing/downloading the contract. */}
          <button
            onClick={handleDownloadContract}
            disabled={downloading}
            className={`w-full mt-5 py-3 rounded-xl font-bold text-white shadow-lg transition flex items-center justify-center gap-2 ${
              downloading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 active:scale-95'
            }`}
          >
            {downloading ? '⏳ ...' : (<><span>📄</span><span>Télécharger le contrat / تحميل العقد</span></>)}
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-green-150 animate-fade-in">
          <div className="text-6xl mb-4 animate-bounce">🎉</div>
          <h1 className="text-2xl font-bold text-green-900 mb-2">
            Merci, votre contrat a été signé avec succès.
          </h1>
          <p className="text-green-700 mb-6 text-lg">شكراً، تم توقيع عقدك بنجاح</p>

          <button
            onClick={handleDownloadContract}
            disabled={downloading}
            className={`w-full py-3.5 mb-3 rounded-xl font-bold text-white shadow-lg transition flex items-center justify-center gap-2 ${
              downloading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 active:scale-95'
            }`}
          >
            {downloading ? '⏳ ...' : (<><span>📄</span><span>Télécharger le contrat de location / تحميل عقد الكراء</span></>)}
          </button>

          <p className="text-sm text-gray-500 mb-4">Vous pouvez fermer cette page. / يمكنك إغلاق هذه الصفحة</p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-650 hover:from-blue-750 hover:to-indigo-750 text-white font-bold rounded-xl shadow-lg transition"
          >
            🏠 Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">✍️</div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Signature du Contrat</h1>
              <p className="text-sm text-gray-500">توقيع عقد الكراء</p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-bold text-gray-650 text-gray-500">🏢 Agence:</span>
              <span className="text-gray-900 font-semibold">{settings.companyName || 'Auto Services'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-bold text-gray-650 text-gray-500">👤 Client:</span>
              <span className="text-gray-900 font-semibold">{rental.clientName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-bold text-gray-650 text-gray-500">🚗 Véhicule:</span>
              <span className="text-gray-900 font-semibold">{rental.carName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-bold text-gray-650 text-gray-500">📅 Période:</span>
              <span className="text-gray-900 font-mono font-semibold">
                {`${rental.startDate} → ${rental.isOpenContract ? 'Ouvert ⏳' : (rental.endDate || '—')}`}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-blue-200">
              <span className="font-bold text-gray-700">💰 Montant total:</span>
              <span className="text-blue-700 font-bold text-lg">
                {`${rental.totalAmount} ${settings.currency || 'MAD'}`}
              </span>
            </div>
          </div>
        </div>
        
        {/* Terms acceptance */}
        <div className="bg-amber-50 border-2 border-amber-250 rounded-xl p-4 mb-4 text-amber-950">
          <h3 className="font-bold text-amber-900 mb-2 text-sm">📋 En signant, vous acceptez:</h3>
          <ul className="text-xs text-amber-800 space-y-1 list-disc pl-5">
            <li>Les conditions générales du contrat de location</li>
            <li>La responsabilité du véhicule pendant la période louée</li>
            <li>L'obligation de restituer le véhicule à la date prévue</li>
            <li>Le paiement du montant total convenu</li>
          </ul>
          <p className="text-xs text-amber-600 mt-2 italic">
            بتوقيعك، أنت توافق على جميع شروط عقد الكراء
          </p>
        </div>
        
        {/* Signature canvas */}
        <div className="bg-white rounded-2xl shadow-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900">✍️ Signez ici / وقّع هنا</h3>
            <button
              onClick={clearCanvas}
              className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-650 text-xs font-bold rounded-lg transition"
            >
              🗑️ Effacer
            </button>
          </div>
          <div 
            className="border-2 border-dashed border-blue-300 rounded-xl bg-gradient-to-b from-white to-blue-50/30 overflow-hidden"
            style={{ touchAction: 'none' }}
          >
            <canvas
              ref={canvasRef}
              className="w-full block cursor-crosshair h-[200px]"
              style={{ touchAction: 'none' }}
            />
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            👆 Utilisez votre doigt ou souris pour signer / استخدم إصبعك للتوقيع
          </p>
        </div>
        
        {/* Submit button */}
        <button
          onClick={submitSignature}
          disabled={signing}
          className={`w-full py-4 rounded-2xl font-bold text-white text-lg shadow-xl transition-all ${
            signing ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-95'
          }`}
        >
          {signing ? '⏳ Enregistrement...' : '✅ Confirmer ma signature / تأكيد التوقيع'}
        </button>
        
        <p className="text-center text-xs text-gray-500 mt-4">
          📞 Pour toute question, contactez {settings.companyName || "l'agence"}
        </p>
      </div>
    </div>
  );
}
